import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { dashboardV2Client } from '../api/client';
import { dashboardV2QueryKeys } from '../api/queryKeys';
import { TimesheetReview, TimesheetReviewEdits } from '../api/types';

/**
 * The message the API actually sent, or null when it sent nothing usable
 * (GRAVY-10812).
 *
 * Reads `errors[0]` then `message`, matching what
 * `API::Admin::DashboardV2::TimesheetReviewController` renders on a 422
 * (`{ success: false, errors: [...] }`). Untyped access on purpose — this runs
 * in a `catch`, where the value is genuinely `unknown` and a thrown Zod parse
 * error, a network failure and an HTTP error all arrive through it.
 *
 * Returns null rather than a generic string so the caller keeps its own
 * per-action fallback: a network drop really is "try again", and that wording
 * should survive for the cases where it is true.
 */
const apiErrorMessage = (e: unknown): string | null => {
  const body = (e as { response?: { data?: unknown } })?.response?.data as
    | { errors?: unknown; message?: unknown }
    | undefined;
  if (!body) return null;

  const first = Array.isArray(body.errors) ? body.errors[0] : undefined;
  const message = typeof first === 'string' ? first : body.message;
  return typeof message === 'string' && message.trim() ? message : null;
};

/**
 * Drives the per-worker Review modal (GRAVY-10366).
 *
 * After a decision it INVALIDATES the dashboard queries rather than reloading
 * the page: a reload discards the tab, the filters, the page and the scroll
 * position — the GRAVY-10330 lesson.
 */
const useTimesheetReview = (agencyId: number) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<TimesheetReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback(
    async (timesheetId: number) => {
      setIsOpen(true);
      setData(null);
      setError(null);
      setLoading(true);
      try {
        setData(
          await dashboardV2Client.getTimesheetReview(agencyId, timesheetId),
        );
      } catch {
        setError('Could not load this timesheet. Close and try again.');
      } finally {
        setLoading(false);
      }
    },
    [agencyId],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setData(null);
    setError(null);
  }, []);

  const decide = useCallback(
    async (run: () => Promise<TimesheetReview>, failure: string) => {
      setLoading(true);
      setError(null);
      try {
        await run();
        // The queue row's status, subtext and flag count all move with this
        // decision, so refetch rather than patch it locally.
        await queryClient.invalidateQueries({
          queryKey: dashboardV2QueryKeys.root,
        });
        setIsOpen(false);
        setData(null);
      } catch (e) {
        // GRAVY-10812. The server's OWN reason, whenever it sent one.
        //
        // This was a bare `catch {}` onto a hardcoded string, so all four of
        // `ApproveTimesheet`'s distinct validation failures — checkout before
        // checkin, zero duration, over 24 hours, break longer than the shift —
        // reached the admin as "Please try again". That is actively misleading:
        // the data is the problem, retrying it unchanged fails the same way,
        // and the message points at the one action that cannot help.
        //
        // `ReviewDecisionService` already unwraps the action's dry-monads
        // failure into a real sentence and the controller renders it under
        // `errors` with a 422; only this layer was discarding it.
        setError(apiErrorMessage(e) ?? failure);
      } finally {
        setLoading(false);
      }
    },
    [queryClient],
  );

  const approve = useCallback(
    (edits: TimesheetReviewEdits) => {
      if (!data) return;
      decide(
        () =>
          dashboardV2Client.approveTimesheet(
            agencyId,
            data.timesheet_id,
            edits,
          ),
        'Could not approve this timesheet. Please try again.',
      );
    },
    [agencyId, data, decide],
  );

  const reject = useCallback(() => {
    if (!data) return;
    decide(
      () => dashboardV2Client.rejectTimesheet(agencyId, data.timesheet_id),
      'Could not reject this timesheet. Please try again.',
    );
  }, [agencyId, data, decide]);

  return { isOpen, data, loading, error, open, close, approve, reject };
};

export default useTimesheetReview;
